import * as React from 'react'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Grid from '@mui/material/Grid'
import AbortButton from '@/components/buttons/AbortButton'
import SuccessButton from '@/components/buttons/SuccessButton'
import TextField from '@mui/material/TextField'
import { useTranslation } from 'react-i18next'
import DeleteIcon from '@mui/icons-material/Delete'
import ActionButton from '@/components/buttons/ActionButton'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import ListSelection, {
  ISelection,
} from '@/components/listSelection/ListSelection'
import moment from 'moment'
import DropDown, { IValue } from '@/components/dropDown/DropDown'
import { IScaleInterval, IExceptions } from './calendarTypes'

interface IPopupDialog {
  title: string | React.ReactNode
  startTimestamp: moment.Moment
  endTimestamp: moment.Moment
  minAllowed?: moment.Moment
  maxAllowed?: moment.Moment
  firstDay: moment.Moment
  comment?: string
  scaleFormat: string
  scaleIntervals: IScaleInterval[]
  selections?: ISelection[]
  isLoading: boolean
  preSelectedPlaceId?: number
  placeData?: IValue[]
  onClose: () => void
  onDelete?: () => void
  onConfirm: (
    startTimestamp: Date,
    endTimestamp: Date,
    comment?: string,
    placeId?: number,
    exceptions?: IExceptions,
  ) => void
}

const PopupDialog: React.FC<IPopupDialog> = ({
  title,
  startTimestamp,
  endTimestamp,
  minAllowed,
  maxAllowed,
  firstDay,
  comment = '',
  selections,
  scaleFormat,
  scaleIntervals,
  isLoading,
  placeData,
  preSelectedPlaceId,
  onClose,
  onDelete,
  onConfirm,
}) => {
  let preSelectedPlace
  if (preSelectedPlaceId && placeData) {
    preSelectedPlace = placeData.find(
      (place) => place.id === preSelectedPlaceId,
    )
  } else if (placeData?.length) {
    preSelectedPlace = placeData[0]
  }
  const alreadyExistIds = selections?.reduce((ids, selection) => {
    if (selection.defaultSelected) {
      ids.push(selection.id)
      return ids
    }
    return ids
  }, [] as number[])
  const { t } = useTranslation('default')
  const [newComment, setComment] = React.useState<string>(comment)
  const [newStartTimestamp, setNewStartTimestamp] =
    React.useState<moment.Moment>(startTimestamp)
  const [newEndTimestamp, setNewEndTimestamp] =
    React.useState<moment.Moment>(endTimestamp)
  const [selected, setSelected] = React.useState<number[]>(alreadyExistIds)
  const [selectedPlaceId, setSelectedPlaceId] = React.useState<
    number | undefined
  >(preSelectedPlaceId)
  const [updateClicked, setUpdateClicked] = React.useState<boolean>(false)
  const minutesStep = 30
  const commentMaxLength = 50
  const startScaleIntervals = scaleIntervals.reduce((list, interval) => {
    if (minAllowed && maxAllowed) {
      if (
        interval.start.isSameOrAfter(minAllowed) &&
        interval.start.isSameOrBefore(maxAllowed)
      ) {
        list.push(interval.start)
      }
    } else {
      list.push(interval.start)
    }
    return list
  }, [])
  const endScaleIntervals = scaleIntervals.reduce((list, interval) => {
    if (minAllowed && maxAllowed) {
      if (
        interval.end.isSameOrAfter(minAllowed) &&
        interval.end.isSameOrBefore(maxAllowed)
      ) {
        list.push(interval.end)
      }
    } else {
      list.push(interval.end)
    }
    return list
  }, [])

  React.useEffect(() => {
    if (placeData?.length) {
      setSelectedPlaceId(placeData[0].id)
    }
  }, [placeData])

  const handleStart = (event: SelectChangeEvent<number>) => {
    const newValue = moment(event.target.value)
    if (newValue.isSameOrAfter(newEndTimestamp, 'minutes')) {
      setNewEndTimestamp(moment(newValue).add(minutesStep, 'minutes'))
    }
    setNewStartTimestamp(newValue)
  }

  const handleEnd = (event: SelectChangeEvent<number>) => {
    const newValue = moment(event.target.value)
    if (newValue.isSameOrBefore(newEndTimestamp, 'minutes')) {
      setNewStartTimestamp(moment(newValue).subtract(minutesStep, 'minutes'))
    }
    setNewEndTimestamp(newValue)
  }

  const getIncludeExcludes = (): IExceptions => {
    if (selections) {
      const includes = selections
        .filter((sel) => !sel.defaultSelected && selected.includes(sel.id))
        .map((sel) => sel.id)
      const excludes = selections
        .filter((sel) => sel.defaultSelected && !selected.includes(sel.id))
        .map((sel) => sel.id)
      if (includes.length || excludes.length)
        return {
          includes,
          excludes,
        }
    }
    return undefined
  }

  const correctDTS = (time: moment.Moment): moment.Moment => {
    // Check if going over to or from daylight saving time.
    const correctedTime = time.clone()
    if (!firstDay.isDST() && correctedTime.isDST()) {
      correctedTime.subtract(1, 'hours')
    } else if (firstDay.isDST() && !correctedTime.isDST()) {
      correctedTime.add(1, 'hours')
    }
    return correctedTime
  }

  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Grid
          container
          direction="column"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={3}
          paddingTop={1}
        >
          <Grid item xs width="100%">
            <FormControl fullWidth>
              <InputLabel>{t('calendar.startTime')}</InputLabel>
              <Select
                value={+moment(newStartTimestamp)}
                label={t('calendar.startTime')}
                onChange={handleStart}
              >
                {startScaleIntervals.map((time, index) => (
                  <MenuItem key={index} value={+time}>
                    {correctDTS(time).format(scaleFormat)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs width="100%">
            <FormControl fullWidth>
              <InputLabel>{t('calendar.endTime')}</InputLabel>
              <Select
                value={+moment(newEndTimestamp)}
                label={t('calendar.endTime')}
                onChange={handleEnd}
              >
                {endScaleIntervals.map((time, index) => (
                  <MenuItem key={index} value={+time}>
                    {correctDTS(time).format(scaleFormat)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {placeData && (
            <Grid item xs width="100%">
              <DropDown
                label={t('place')}
                defaultValue={preSelectedPlace}
                values={placeData}
                onChange={(newValue) => setSelectedPlaceId(newValue.id)}
              />
            </Grid>
          )}

          <Grid item xs width="100%">
            <TextField
              sx={{
                marginTop: '10px',
                width: '100%',
              }}
              helperText={`${newComment.length} / ${commentMaxLength}`}
              value={newComment}
              label={t('calendar.comment')}
              variant="outlined"
              inputProps={{ maxLength: commentMaxLength }}
              multiline
              rows={3}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setComment(event.target.value)
              }}
            />
          </Grid>

          {!onDelete && selections && (
            <Grid item xs width="100%">
              <div>{t('calendar.addRemoveUsers')}</div>
              <div>
                <i>* {t('calendar.isAddedInScene')}</i>
              </div>
              <ListSelection
                selected={selected}
                selections={selections}
                onChange={(newSelected) => setSelected(newSelected)}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <AbortButton onClick={onClose} disabled={isLoading} />
        {onDelete && (
          <ActionButton
            label={t('calendar.delete')}
            icon={<DeleteIcon />}
            isLoading={!updateClicked && isLoading}
            disabled={isLoading}
            onClick={() => onDelete && onDelete()}
          />
        )}
        <SuccessButton
          isLoading={(!onDelete || updateClicked) && isLoading}
          disabled={(onDelete || !updateClicked) && isLoading}
          label={onDelete ? t('calendar.update') : t('calendar.save')}
          onClick={() => {
            onConfirm(
              newStartTimestamp.toDate(),
              newEndTimestamp.toDate(),
              newComment,
              selectedPlaceId,
              getIncludeExcludes(),
            )
            setUpdateClicked(true)
          }}
        />
      </DialogActions>
    </Dialog>
  )
}

export default PopupDialog
